/**
 * Lazy-loaded icons with encapsulated Suspense boundaries.
 */
import React, { Suspense } from 'react';

// oxlint-disable-next-line @atlassian/no-restricted-imports
import { lazyForPaint } from '@atlassian/react-loosely-lazy';

import { withBrandIcon, type BrandIcon } from '../../../icons/with-brand-icon';

const OfficialIcon = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "@atlassian/logo-third-party/webex" */ '@atlassian/logo-third-party/webex'
		).then((m) => m.WebexIcon),
	{ moduleId: '@atlassian/logo-third-party/webex' },
);

const LazyWebexIconComponent = lazyForPaint(
	() => import(/* webpackChunkName: "icon-webex" */ './utils').then((m) => m.WebexIcon),
	{ moduleId: '@atlassian/rovo-connectors-config/webex/icon' },
);

const LazyWebexSearchResultIconComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-webex-search-result" */ './utils').then(
			(m) => m.WebexSearchResultIcon,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/webex/search-result-icon' },
);

const LazyWebexIconBorderlessComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-webex-borderless" */ './utils').then(
			(m) => m.WebexIconBorderless,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/webex/icon-borderless' },
);

/**
 * Exported icons with self-contained Suspense boundaries.
 * Consumers can use these directly without wrapping in LazySuspense.
 */
export const WebexIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyWebexIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'small',
);

export const WebexSearchResultIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyWebexSearchResultIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'medium',
);

export const WebexIconBorderless: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyWebexIconBorderlessComponent />
		</Suspense>
	),
	OfficialIcon,
	'xxsmall',
);
